import { Settings } from '@streamr/tv-engine';
import { EventEmitter } from 'eventemitter3';
import Worker from 'web-worker';
import { Env } from '../../Env';
import { createWorker } from '../../utils/workerUtils';
import { FrameExtractor } from './FrameExtractor';
export class VideoProducer extends EventEmitter {
    videoEncoderWorker;
    videoFrameExtractor;
    track;
    frameRate = 0;
    frameDurationMicros = 0;
    onEncodedVideoData(videoData) {
        videoData.timestamp = BigInt(videoData.timestamp);
        videoData.duration = BigInt(this.frameDurationMicros);
        this.emit('encodedVideoData', videoData);
    }
    startWorker(frameRate, forceFrameRate = false, videoTrack) {
        // Not DRY, but otherwise webpack won't transform the worker url
        if (Env.BUILD_TRANSFORMS_FILENAMES) {
            this.videoEncoderWorker = new Worker(new URL('./workers/videoencoder.worker.ts', import.meta.url));
        }
        else {
            this.videoEncoderWorker = createWorker('./workers/videoencoder.worker.js', import.meta.url);
        }
        this.videoEncoderWorker.onmessage = (e) => {
            const { type } = e.data;
            switch (type) {
                case 'encodedVideoData':
                    this.onEncodedVideoData(e.data.data);
                    break;
                case 'videoFrame':
                    this.emit('videoFrame', e.data.videoFrame);
                    break;
                case 'startCompleted':
                    this.emit('workerStarted');
                    break;
                case 'stopCompleted':
                    this.emit('workerStopped');
                    break;
            }
        };
        return new Promise((resolve, _reject) => {
            this.once('workerStarted', () => {
                resolve();
            });
            this.videoEncoderWorker?.postMessage({
                type: 'start',
                frameRate: frameRate,
                forceFrameRate: forceFrameRate,
                videoTrack: videoTrack,
            });
        });
    }
    async start(videoTrack) {
        this.track = videoTrack;
        // Create FrameExtractor in worker on Safari
        // in main thread on Chrome
        const createFrameExtractorInWorker = typeof MediaStreamTrackProcessor === 'undefined';
        console.log('start createFrameExtractorInWorker', createFrameExtractorInWorker);
        this.frameRate =
            videoTrack.getSettings().frameRate ??
                Settings.videoEncoderDefaultFrameRate;
        this.frameDurationMicros = Math.round(1_000_000 / this.frameRate);
        const forceFrameRate = videoTrack.getSettings().displaySurface != null;
        console.log('frameDurationMicros', this.frameDurationMicros);
        await this.startWorker(this.frameRate, forceFrameRate, createFrameExtractorInWorker ? videoTrack : undefined);
        if (!createFrameExtractorInWorker) {
            this.videoFrameExtractor = new FrameExtractor(videoTrack);
            this.videoFrameExtractor.emitter.on('frame', (frame) => {
                this.emit('videoFrame', frame);
            });
            await this.videoFrameExtractor.start();
        }
    }
    feedVideoFrame(videoFrame) {
        this.videoEncoderWorker?.postMessage({
            type: 'feedVideoFrame',
            videoFrame: videoFrame,
        }, [videoFrame]);
    }
    async stop() {
        if (this.videoFrameExtractor) {
            await this.videoFrameExtractor.stop();
            this.videoFrameExtractor = undefined;
        }
        if (this.videoEncoderWorker) {
            return new Promise((resolve, _reject) => {
                this.once('workerStopped', () => {
                    this.videoEncoderWorker?.terminate();
                    this.videoEncoderWorker = undefined;
                    resolve();
                });
                this.videoEncoderWorker?.postMessage({
                    type: 'stop',
                });
            });
        }
    }
    startBroadcasting() {
        this.videoEncoderWorker?.postMessage({
            type: 'startBroadcasting',
        });
    }
    stopBroadcasting() {
        this.videoEncoderWorker?.postMessage({
            type: 'stopBroadcasting',
        });
    }
    setVideoEncodingBitrate(bitrate) {
        this.videoEncoderWorker?.postMessage({
            type: 'setVideoEncodingBitrate',
            bitrate: bitrate,
        });
    }
    getTrack() {
        if (!this.track) {
            throw new Error('No track information. Call `start` first.');
        }
        return this.track;
    }
}
